
import MarkerCluster from "@/components/MarkerCluster.component.vue";
import { getCoordinates, getSamplingPointById } from "@/service/samplingpoint.service";
import { Container } from "@/types/container.type";
import { SamplingPoint } from "@/types/samplingpoint.type";
import { RadToDeg, UTMXYToLatLon } from "@/utils/coordinates.util";
import { errorHandlerDefault } from "@/utils/handler.util";
import { LMap, LMarker, LTileLayer, LTooltip } from "@vue-leaflet/vue-leaflet";
import { ElMessage } from "element-plus";
import { LatLngLiteral, Map } from "leaflet";
import "leaflet/dist/leaflet.css";
import { debounce } from "lodash";
import { defineComponent } from "vue";
import { Refresh } from "@element-plus/icons-vue";

interface SamplingPointMap extends SamplingPoint {
  latlon?: [number, number];
}

const defPosition: LatLngLiteral = { lat: 40.41297, lng: -3.70296 };

export default defineComponent({
  props: {
    containerId: {
      type: Number,
      required: false,
    },
    detailRouterName: {
      type: String,
    },
    onClick: {
      type: Function,
      required: false,
    },
    minimap: {
      type: Boolean,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    LMap,
    LMarker,
    LTileLayer,
    LTooltip,
    MarkerCluster,
    //SamplingPointMapImageComponent,
  },

  setup() {
    const firstPosition = defPosition;
    const dataReady = false;
    const mapOptions = { attributionControl: false };

    return {
      defPosition,
      firstPosition,
      dataReady,
      mapOptions,
      Refresh,
    };
  },
  data() {
    return {
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      zoom: 6,
      leafletReady: false,
      leafletObject: {} as Map,
      samplingPoints: [] as SamplingPointMap[],
      samPointsMap: [] as SamplingPointMap[],
      allSamPointsMap: [] as SamplingPointMap[],
      samPoint: {} as SamplingPoint,
      query: "" as string,
      showTooltip: false,
      id: {} as number | undefined,
    };
  },
  async created() {
    if (this.minimap) {
      this.zoom = 19;
    }
    // - apply delay to search
    this.search = debounce(() => {
      this.refreshData().catch(errorHandlerDefault);
    }, 1200);

    // - init data
    await this.refreshData();
  },
  computed: {
    filteredSamPointsMap() {
      return this.samPointsMap.filter((samplingPoint) => samplingPoint.latlon);
    },
  },
  methods: {
    search(): void {
      console.log("Search", this.query);
      this.refreshData();
    },
    onClear() {
      console.log("Clear", this.query);
      this.query = "";
      this.search();
    },
    async onLeafletReady() {
      await this.$nextTick();
      this.leafletObject = (this.$refs.map as InstanceType<typeof LMap>).leafletObject;
      this.leafletReady = true;

      // - move if data is ready
      // if (this.dataReady) {
      //   this.flyTo(this.firstPosition, false);
      // }
    },

    async openSamplingPointDetail(samplingPoint: SamplingPoint) {
      if (samplingPoint.id !== undefined) {
        const samPoint = await getSamplingPointById(samplingPoint.id);

        const container = samPoint.container as Container | undefined;

        if (
          container?.id &&
          this.$route.path.includes("support") &&
          !this.$route.path.includes("samplingPoints/")
        ) {
          if (this.$route.path.includes("samplingPoints")) {
            this.$router.push("samplingPoints/" + container?.id + "?samplingPoint=" + samPoint.id);
          } else {
            this.$router.push(
              "/support/samplingPoints/" + container?.id + "?samplingPoint=" + samPoint.id
            );
          }
        } else if (container?.id && this.detailRouterName) {
          this.$router.push({
            name: this.detailRouterName,
            params: { containerid: container.id },
          });
        } else if (this.onClick !== undefined) {
          this.onClick(samPoint);
        }
      }
    },
    open(samplingPoint: SamplingPoint) {
      if (this.id !== undefined) {
        this.id = samplingPoint.id;

        this.showTooltip = true;
      }
    },
    close() {
      if (this.showTooltip === true) {
        this.showTooltip = false;
      }
    },

    async refreshData() {
      // - get mapSamplingPoints
      if (this.samPointsMap.length === 0) {
        // No hay datos en this.samPointsMap, por lo que realizamos la consulta
        this.allSamPointsMap = await getCoordinates();
      }

      const searchResult = this.allSamPointsMap.filter((samplingPoint) => {
        return samplingPoint.name?.toLowerCase().includes(this.query.toLowerCase());
      });

      this.samPointsMap = searchResult;

      // - append position
      this.samPointsMap.forEach((_) => {
        if (_.utmX && _.utmY && _.timeZone) {
          const latlon = new Array(2);
          UTMXYToLatLon(_.utmX, _.utmY, _.timeZone, "N", latlon);
          _.latlon = [RadToDeg(latlon[0]), RadToDeg(latlon[1])];
        }
      });

      if (this.samPointsMap.length !== 0) {
        // - set first position
        this.firstPosition = this.getFirstPosition();

        // - move if leaflet is ready
        //cometado para centrar el mapa
        // if (this.leafletReady) {
        //   this.flyTo(this.firstPosition, this.dataReady);
        // }
      } else {
        ElMessage.info(this.$t("info.samplingPointsNotFound"));
      }
      this.dataReady = true;
    },

    /**
     * get first position
     * @returns
     */
    getFirstPosition(): LatLngLiteral {
      return this.samPointsMap[0]?.latlon
        ? { lat: this.samPointsMap[0].latlon[0], lng: this.samPointsMap[0].latlon[1] }
        : this.firstPosition;
    },

    /**
     * move map viewport
     * @param position
     * @param animate
     */
    flyTo(position: LatLngLiteral, animate = true) {
      this.leafletObject.flyTo(position, this.leafletObject.getZoom(), { animate });
    },
  },
});
